/**
 * The company SOURCE master — where an account came from — and the one rule for
 * turning it into picker options.
 *
 * ## Why this module exists
 *
 * It is `use-company-stages.ts` for a second master, deliberately line for line
 * rather than parameterised. The stage master grew three copies of its fetch and
 * its option builder across the list, the detail page and the form; all three
 * were correct and **two of them disagreed about how**, which is the state that
 * drifts into two different answers to the same question. One module per master
 * so they cannot.
 *
 * ⚠️ **Copied, not generalised, and that was a choice.** A shared
 * `useReferenceMaster(kind)` looks like the tidier answer and would be — right
 * up until one master needs a rule the other must not have. The two already
 * differ on the wire (see *Retired sources* below), so a single implementation
 * would have to carry a branch, and a branch inside a shared hook is where two
 * masters start quietly borrowing each other's behaviour.
 *
 * ## The fetch: no `isActive` filter, split at render
 *
 * `?isActive=true` gives a clean create dropdown and then drops the source a
 * company already holds the day an admin retires it. Fed only active rows, the
 * control renders blank for that company — and a stray save of the blank value
 * silently rewrites where an account came from. Injecting the stored value back
 * covers that, but only ever with the **stale copy of the name** carried on the
 * company record: retire and rename a source, and the injected option still
 * shows the old label while the master says something else.
 *
 * Fetching everything and splitting here has neither problem — the master's
 * current name always wins — and it is the rule `CLAUDE.md` already documents
 * for the deal pickers, for the same reason.
 *
 * ## Retired sources: MARKED, never removed
 *
 * A retired row a company still holds is shown and labelled, exactly as a
 * retired stage is. **That half is not a guess about the server** — it is
 * forced from this side regardless of what the write schema does, because the
 * alternative is a picker that renders blank over a value the record really
 * holds, and a company quietly reclassified by the next save of a field
 * somebody opened for an unrelated reason.
 *
 * ⚠️ **What IS unmeasured here is whether `sourceId` accepts an INACTIVE id on
 * a write.** `stageId` is checked for existence only — verified on the wire,
 * and deliberate on the backend's part so an inline editor in a list cannot
 * fail on a row whose stage an admin retired yesterday. Nothing has established
 * that for this key. **Do not infer it from the stage's behaviour**: the
 * deliverables picklists 422 on an inactive master and `facultyId` on a company
 * does too, so both answers are live on this API. Until somebody drives it, a
 * caller must be ready for a 422 naming `sourceId` on a re-save of a retired
 * row, and must not disable the option pre-emptively — withholding a real value
 * is the worse failure of the two.
 */

import { useCallback, useEffect, useState } from 'react';
import { api } from './api';
import type { MasterRef, ReferenceRecord } from './types';

/** `perPage` is the endpoint's ceiling; 201 is a 422, and the default 50 would
 * silently page a master that outgrew it. There is **no `sort` / `order`
 * parameter at all** — the query schema is `.strict()`, ordering is always
 * `sortOrder, name`, and sending one is a 422. Both verified on the wire for
 * the sibling master this shares an implementation with. */
const SOURCE_QUERY = { page: 1, perPage: 200 } as const;

export interface CompanySourceOption {
  value: string;
  label: string;
}

export interface CompanySourcesState {
  records: ReferenceRecord[];
  loading: boolean;
  /**
   * True when the master could not be read.
   *
   * Kept separate from an empty list on purpose: a caller must be able to say
   * "these could not be loaded" rather than offering a control with one option
   * in it, which reads as "there are no sources" and invites clearing a real
   * value.
   */
  failed: boolean;
  /**
   * Read the master again.
   *
   * Exists because a failed load must be retryable: the alternative is telling
   * someone the sources could not be loaded and offering them no way to try,
   * which turns a blip into a reload of the whole page.
   */
  reload: () => void;
}

/**
 * Reads the master once per mount.
 *
 * `enabled` exists so a read-only surface can skip the request entirely — a
 * page that renders the source as text has nothing to fill a picker with. It
 * reports `loading: false` when disabled, because "not asked for" is settled,
 * not pending.
 * ⚠️ *SUPERSEDED: "the rate limit is 120/60s shared across the whole office."*
 * It is **12,000/60s** (corrected 2026-09-17). Skipping the request is still
 * right — it is a request nothing on the page can use — but the arithmetic that
 * reason cited is off by a factor of a hundred.
 */
export function useCompanySources(enabled = true): CompanySourcesState {
  const [records, setRecords] = useState<ReferenceRecord[]>([]);
  const [loading, setLoading] = useState(enabled);
  const [failed, setFailed] = useState(false);
  const [nonce, setNonce] = useState(0);

  const reload = useCallback(() => {
    setFailed(false);
    setLoading(true);
    setNonce((value) => value + 1);
  }, []);

  useEffect(() => {
    if (!enabled) return;
    const controller = new AbortController();
    api
      .listCompanySources(SOURCE_QUERY, controller.signal)
      .then((result) => {
        if (controller.signal.aborted) return;
        setRecords(result.data);
        setFailed(false);
      })
      .catch(() => {
        if (controller.signal.aborted) return;
        setRecords([]);
        setFailed(true);
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [enabled, nonce]);

  return { records, loading: enabled ? loading : false, failed, reload };
}

/**
 * How a source's name is displayed anywhere in the app.
 *
 * A blank name would render an invisible option and an empty cell — the master
 * trims to 1..60 so it should not happen, but a name is user-entered data and
 * "should not happen" is not a rendering strategy. The id is at least
 * selectable and identifies the row to whoever has to fix it.
 */
export function companySourceLabel(id: number, name: string): string {
  const trimmed = name.trim();
  return trimmed === '' ? `#${id}` : trimmed;
}

const labelFor = companySourceLabel;

/**
 * The options one picker should offer: every active source, plus `current` when
 * it is no longer active — because a value the record actually holds must
 * always be visible.
 *
 * The two retired wordings are not decoration. **"(retired)"** means the master
 * still has the row and we can read its real name. **"(no longer offered)"**
 * means the row is not in the list at all, so the only name available is the
 * stale one stored on the record.
 *
 * And an empty `records` gets **neither** label: an empty list means "still
 * loading" or "failed to load", never "every source was retired". Labelling
 * then would accuse a perfectly live source of being retired for a frame or
 * two.
 *
 * ⚠️ **There is no "not recorded" row here, and a caller must supply its own.**
 * `null` is the ordinary state on this column — most companies have no source —
 * so the empty choice is not an edge a picker may omit. It belongs to the
 * control, which is the only thing that knows whether clearing sends `null` or
 * whether this is a filter where absence means "any".
 */
export function companySourceOptions(
  records: ReferenceRecord[],
  current: MasterRef | null,
): CompanySourceOption[] {
  const options = records
    .filter((record) => record.isActive || record.id === current?.id)
    .map((record) => ({
      value: String(record.id),
      label: record.isActive
        ? labelFor(record.id, record.name)
        : `${labelFor(record.id, record.name)} (retired)`,
    }));

  if (current && !options.some((option) => option.value === String(current.id))) {
    options.unshift({
      value: String(current.id),
      label:
        records.length > 0
          ? `${labelFor(current.id, current.name)} (no longer offered)`
          : labelFor(current.id, current.name),
    });
  }

  return options;
}
